import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardDocumentListIcon, CheckCircleIcon, ClockIcon, ExclamationTriangleIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../../components/Layout';
import LocationBanner from '../../components/LocationBanner';
import api from '../../utils/api';

const C = { teal:'#00B4D8', navy:'#0D1B2A', success:'#10B981', warning:'#F59E0B', error:'#EF4444', grey:'#6B7280', border:'#E5E7EB', bg:'#F0F7FA' };

const STATUS_CONFIG = {
  pending:   { label:'Pending',   color:'#3B82F6', bg:'#EFF6FF', icon:'📋' },
  late:      { label:'Overdue',   color:'#EF4444', bg:'#FEF2F2', icon:'⚠️' },
  submitted: { label:'Submitted', color:'#10B981', bg:'#ECFDF5', icon:'✅' },
  reviewed:  { label:'Reviewed',  color:'#8B5CF6', bg:'#F5F3FF', icon:'⭐' },
};

const TYPE_ICONS = {
  grammar:'📚', vocabulary:'📖', reading:'📰', writing:'✍️',
  conversation:'💬', ielts:'🎯', business:'💼',
};

function HwCard({ hw, onStart }) {
  const cfg = STATUS_CONFIG[hw.status] || STATUS_CONFIG.pending;
  const sub = hw.submission;
  const due = hw.due_date ? new Date(hw.due_date) : null;
  const dueStr = due ? due.toLocaleDateString('en-GB', { day:'numeric', month:'short' }) : '';
  const isOverdue = hw.status === 'late';
  const canDo = hw.status === 'pending' || hw.status === 'late';

  return (
    <motion.div
      initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}
      className="card"
      style={{ padding:20, marginBottom:12, borderLeft:`4px solid ${cfg.color}`, cursor: canDo ? 'pointer' : 'default' }}
      onClick={() => canDo && onStart(hw)}
    >
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div style={{ flex:1 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
            <span style={{ fontSize:18 }}>{TYPE_ICONS[hw.lesson_type] || '📝'}</span>
            <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:C.navy }}>{hw.title}</h3>
          </div>
          <div style={{ fontSize:12, color:C.grey, marginBottom:8 }}>
            {hw.generated_by === 'AI' ? '🤖 AI Generated' : `👩‍🏫 Set by ${hw.generated_by}`}
            {dueStr && <span style={{ marginLeft:12 }}>📅 Due: <strong style={{ color: isOverdue ? C.error : 'inherit' }}>{dueStr}{isOverdue ? ' (Overdue)' : ''}</strong></span>}
          </div>
          <p style={{ margin:0, fontSize:13, color:C.grey }}>{hw.instructions}</p>

          {sub && (
            <div style={{ marginTop:12, padding:'10px 14px', background:cfg.bg, borderRadius:8 }}>
              <div style={{ fontWeight:700, fontSize:13, color:cfg.color, marginBottom:4 }}>
                Score: {sub.score}/{hw.max_score} ({sub.percentage}%)
              </div>
              {sub.ai_feedback && (
                <p style={{ margin:'4px 0 0', fontSize:12, color:C.grey, lineHeight:1.5 }}>{sub.ai_feedback}</p>
              )}
              {sub.tutor_feedback && (
                <div style={{ marginTop:8, padding:'8px 10px', background:'#F5F3FF', borderRadius:6, fontSize:12 }}>
                  <strong style={{ color:'#8B5CF6' }}>Tutor feedback:</strong> {sub.tutor_feedback}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:8, marginLeft:16 }}>
          <span style={{ padding:'4px 10px', borderRadius:20, fontSize:12, fontWeight:600, color:cfg.color, background:cfg.bg }}>
            {cfg.icon} {cfg.label}
          </span>
          {canDo && (
            <button
              onClick={(e) => { e.stopPropagation(); onStart(hw); }}
              style={{ padding:'8px 16px', background:C.teal, color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer' }}>
              Start →
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function HomeworkPage() {
  const [homework, setHomework] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/student/homework')
      .then(r => {
        setHomework(r.data.homework || []);
        setPendingCount(r.data.pending_count || 0);
      })
      .catch(() => setHomework([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = homework.filter(hw => {
    if (filter === 'pending') return hw.status === 'pending' || hw.status === 'late';
    if (filter === 'done') return hw.status === 'submitted' || hw.status === 'reviewed';
    return true;
  });

  return (
    <div style={{ background:C.bg, minHeight:'100vh' }}>
      {/* Header */}
      <header style={{ background:C.navy, color:'#fff', padding:'0 32px', height:64, display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ fontWeight:800, fontSize:20 }}>SpeakIQ</div>
        <nav style={{ display:'flex', gap:24 }}>
          {[['Dashboard','/dashboard'],['Homework','/homework'],['Vocabulary','/vocabulary'],['Certificates','/my-certificates']].map(([label, path]) => (
            <a key={path} href={path} style={{ color: path === '/homework' ? '#00B4D8' : 'rgba(255,255,255,0.8)', textDecoration:'none', fontSize:14, fontWeight: path === '/homework' ? 700 : 500 }}>{label}</a>
          ))}
        </nav>
        <button onClick={() => { localStorage.clear(); navigate('/login'); }} style={{ background:'rgba(255,255,255,0.1)', border:'none', color:'#fff', padding:'8px 16px', borderRadius:8, cursor:'pointer', fontSize:14 }}>Logout</button>
      </header>

      <div style={{ maxWidth:800, margin:'0 auto', padding:'32px 24px' }}>
        {/* Location Banner */}
        <LocationBanner />

        {/* Hero */}
        <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} style={{ background:`linear-gradient(135deg, ${C.navy} 0%, #1e3a5f 100%)`, borderRadius:16, padding:'24px 28px', marginBottom:24, color:'#fff' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
            <ClipboardDocumentListIcon style={{ width:28, height:28, color:'#00B4D8' }} />
            <h2 style={{ margin:0, fontSize:22, fontWeight:800 }}>My Homework</h2>
          </div>
          <p style={{ margin:'0 0 16px', opacity:0.75, fontSize:14 }}>
            Complete your exercises at home to reinforce today's learning. Your tutor reviews your submissions before class.
          </p>
          <div style={{ display:'flex', gap:24 }}>
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:26, fontWeight:800, color:'#F59E0B' }}>{pendingCount}</div>
              <div style={{ fontSize:12, opacity:0.7 }}>Pending</div>
            </div>
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:26, fontWeight:800, color:'#10B981' }}>{homework.filter(h=>h.status==='submitted'||h.status==='reviewed').length}</div>
              <div style={{ fontSize:12, opacity:0.7 }}>Completed</div>
            </div>
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:26, fontWeight:800, color:'#00B4D8' }}>{homework.length}</div>
              <div style={{ fontSize:12, opacity:0.7 }}>Total</div>
            </div>
          </div>
        </motion.div>

        {/* Filters */}
        <div style={{ display:'flex', gap:8, marginBottom:20 }}>
          {[['all','All'],['pending','Pending'],['done','Completed']].map(([val, label]) => (
            <button key={val} onClick={() => setFilter(val)} style={{
              padding:'8px 18px', borderRadius:20, border:'none', cursor:'pointer', fontWeight:600, fontSize:13,
              background: filter===val ? C.navy : '#fff', color: filter===val ? '#fff' : C.grey,
              boxShadow: filter===val ? 'none' : '0 1px 4px rgba(0,0,0,0.08)'
            }}>{label}</button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div style={{ textAlign:'center', padding:60, color:C.grey }}>
            <SparklesIcon style={{ width:32, height:32, margin:'0 auto 12px', display:'block', opacity:0.4 }} />
            <div>Loading your homework...</div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign:'center', padding:60, color:C.grey, background:'#fff', borderRadius:12 }}>
            <CheckCircleIcon style={{ width:40, height:40, margin:'0 auto 12px', display:'block', color:'#10B981', opacity:0.7 }} />
            <div style={{ fontWeight:700, fontSize:16, marginBottom:8 }}>
              {filter === 'pending' ? 'No pending homework! Great work 🎉' : 'No homework yet'}
            </div>
            <p style={{ margin:0, fontSize:13 }}>
              {filter === 'pending'
                ? 'Complete an AI practice session to receive your next assignment.'
                : 'Homework is generated automatically after each AI practice session.'}
            </p>
          </div>
        ) : (
          <AnimatePresence>
            {filtered.map(hw => (
              <HwCard key={hw.id} hw={hw} onStart={(h) => navigate(`/homework/${h.id}`)} />
            ))}
          </AnimatePresence>
        )}

        {/* Info box */}
        <div style={{ marginTop:24, padding:'16px 20px', background:'#EFF6FF', borderRadius:10, border:'1px solid #BFDBFE' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, fontWeight:700, fontSize:13, color:'#1D4ED8', marginBottom:6 }}>
            <SparklesIcon style={{ width:16, height:16 }} />
            How homework works
          </div>
          <ul style={{ margin:'0', paddingLeft:18, fontSize:12, color:'#1E40AF', lineHeight:1.8 }}>
            <li>Homework is automatically generated after each AI practice session</li>
            <li>Questions are based on exactly what you practised — no surprises</li>
            <li>Submit before your next class so your tutor can review your progress</li>
            <li>Your tutor will go over any mistakes with you in class</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
